// The v2 app client (spec §13.14): one per signed-in v2 athlete. It keeps
//   - `cal`: the calendar and thread as the stream builds them (reducer.js),
//     persisted so the app opens on what it last knew;
//   - `pending`: the athlete's commands, persisted and sent in order, each
//     with its Idempotency-Key, so an edit made offline replays on reconnect
//     and a retry changes nothing; a command is pending until the stream
//     carries its effect, and a refusal (409, 422) rolls it back into
//     `failed` (failures.js words it: blame-free, with Try again, and the
//     backend's reason only under Details). A 409 on a versioned command is
//     retried once with the current version when every change since its base
//     was this client's own (an offline queue replaying), never over a change
//     made elsewhere; a 5xx/429/network failure is tried again on a backoff
//     timer, and Try again after one reuses its key; deleting what is already
//     gone (404) is a success; a second identical remove or move of one
//     session while the first is on its way is ignored;
//   - `outcomes`: per thread item, what its command answered when that was
//     words rather than a plain success: an accept the coach is repairing
//     ('updating') or couldn't apply ('kept_current'), a rebuild asked for;
//     persisted, so stale buttons don't come back after a reload;
//   - `live`: the coach's streaming text and status line per run.
// React reads it through useSyncExternalStore (subscribe / getSnapshot).
//
// The stream: GET /v2/stream?after_seq=<ours>. A gap, a 409, a reconnect or the
// tab coming back syncs first (GET /v2/sync); a snapshot answer means we were
// too far behind, and the calendar is read again. Backoff with jitter; a
// stream silent past STALL_MS (the backend pings every 15 s) is dropped.
//
// `readOnly`: a human coach viewing the athlete (viewing.js). The reads and
// the stream run as usual (the api passed in carries athlete_id); nothing is
// queued or sent, and no queue is restored.

import { apply, fromCalendar, SeqGap } from './reducer';
import { DONE_STAGES, strategyFromReads } from './strategy';
import { readSSE } from '../components/chat/sse';
import { memoryPersist } from './persist';
import { AI_CONSENT_FAILURE_TEXT, isAiConsentRequired } from '../lib/aiConsent';
import { alreadyGone, backendReason, collidingSessions, commandOf, currentVersion, duplicateOf, goneFrom, isProposalStale, outcomeFor, PROPOSAL_STALE_TEXT, REBUILD_KINDS, settledConflict } from './failures';
import { commands as v2Commands } from './commands';
import { PLAN_BUILD_ID } from './calendarRoutes';

export const STALL_MS = 45 * 1000;
const BACKOFF_MS = [1000, 2000, 5000, 10000, 30000];
const SETTLE_AFTER_MS = 20 * 1000;      // a confirmed edit no screen settled drops anyway
const MAX_TRANSIENT_TRIES = 5;          // 5xx: retried this often, then refused
const REFUSED = new Set([400, 403, 404, 409, 410, 422]);

const isoDay = (d) => d.toISOString().slice(0, 10);

/** The window the client keeps in full: two weeks back, fifteen ahead (/v2/calendar allows 120 days). */
export function storeWindow(now = new Date()) {
  const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7) - 14);
  const to = new Date(d);
  to.setUTCDate(to.getUTCDate() + 118);
  return { from: isoDay(d), to: isoDay(to) };
}

const MAX_OUTCOMES = 100;
/** The outcomes worth keeping: the latest MAX_OUTCOMES (insertion order is answer order). */
export function keepOutcomes(outcomes) {
  const entries = Object.entries(outcomes || {});
  return Object.fromEntries(entries.slice(Math.max(0, entries.length - MAX_OUTCOMES)));
}

/** An event `apply` threw on (not a gap): the client reads the calendar again rather than replay it forever. */
export class BadEvent extends Error {
  constructor(event, cause) {
    super(`could not apply event ${event?.seq} (${event?.type}): ${cause?.message || cause}`);
    this.event = event;
    this.cause = cause;
  }
}

export const newKey = () => (globalThis.crypto?.randomUUID?.() || `k-${Date.now()}-${Math.random().toString(16).slice(2)}`);

/**
 * The coach's live state per run, from one event: `coach.text` deltas build
 * the streaming bubble, `coach.step` the trail of what it has looked at,
 * `run.status` the status line; the run's reply (its coach_message), its
 * proposal, or a status other than 'started' ends it.
 */
export function applyLive(prev, event) {
  const p = event.payload || {};
  if (event.type === 'coach.text' && p.run_id) {
    const run = prev[p.run_id] || {};
    return { ...prev, [p.run_id]: { ...run, text: (run.text || '') + (p.delta || '') } };
  }
  if (event.type === 'coach.step' && p.run_id && p.label) {
    const run = prev[p.run_id] || {};
    const steps = run.steps || [];
    return steps.at(-1) === p.label ? prev : { ...prev, [p.run_id]: { ...run, steps: [...steps, p.label] } };
  }
  if (event.type === 'run.status' && p.run_id) {
    if (p.state !== 'started') {
      const next = { ...prev };
      delete next[p.run_id];
      return next;
    }
    return { ...prev, [p.run_id]: { ...(prev[p.run_id] || {}), label: p.label, stage: p.stage } };
  }
  // A creation run never says it finished: its proposal (or the draft's end) is the end of it.
  const ended = event.type === 'draft.status' && DONE_STAGES.has(p.stage) ? event.run_id
    : event.type === 'thread.item' && p.type === 'proposal' ? p.refs?.agent_run_id : null;
  let next = prev;
  if (ended && prev[String(ended)]) {
    next = { ...prev };
    delete next[String(ended)];
  }
  // A plan build that ended (its draft done, or the coach saying it couldn't
  // build or apply it) ends every status-only run: the build's own run may
  // never say it finished, and its end can come under another run's id
  // (a plan_failed after "Your plan is ready", 2026-10-25), which left the
  // header saying the plan was ready under the failure. The draft's own run
  // ending above doesn't skip this: another run's line stayed up (29 Oct review).
  if (endsPlanBuild(event)) {
    const idle = Object.keys(next).filter((run) => !next[run].text);
    if (idle.length) {
      if (next === prev) next = { ...prev };
      idle.forEach((run) => delete next[run]);
    }
  }
  if (next !== prev) return next;
  if (event.type === 'thread.item' && p.type === 'coach_message' && p.refs?.agent_run_id && prev[p.refs.agent_run_id]) {
    const next = { ...prev };
    delete next[p.refs.agent_run_id];   // the reply landed: the streamed text was its preview
    return next;
  }
  return prev;
}

/**
 * An event after which GET /v2/strategy reads differently (M6): a change
 * applied to the plan (a week written, a phase review's change), the coach's
 * on-track message, a phase review.
 */
export function changesStrategy(event) {
  if (event?.type === 'change.applied') return true;
  if (event?.type !== 'thread.item') return false;
  const b = event.payload?.body || {};
  return (event.payload.type === 'coach_message' && b.kind === 'on_track')
    || (event.payload.type === 'plan_update' && (b.kind === 'phase_review' || b.kind === 'week_written'));
}

/**
 * An event that ends a plan build: its draft is proposed (or answered), or the
 * coach says it couldn't build or apply it. The calendar's rebuild outcome
 * (calendarRoutes.PLAN_BUILD_ID) is dropped then, so Regenerate Plan is
 * offered again rather than "on it" for good.
 */
export function endsPlanBuild(event) {
  const p = event?.payload || {};
  if (event?.type === 'draft.status') return DONE_STAGES.has(p.stage);
  return event?.type === 'thread.item' && p.type === 'coach_message' && REBUILD_KINDS.includes(p.body?.kind);
}

export function createV2Client({
  userId, api, persist = memoryPersist(), readOnly = false, win = globalThis.window, now = () => new Date(),
  sleep = (ms) => new Promise((r) => setTimeout(r, ms)), random = Math.random,
  schedule = (fn, ms) => setTimeout(fn, ms), unschedule = (t) => clearTimeout(t),
}) {
  const K = { cal: `v2:${userId}:cal`, queue: `v2:${userId}:queue`, outcomes: `v2:${userId}:outcomes` };
  let state = { status: 'starting', cal: null, pending: [], failed: [], outcomes: {}, live: {}, window: storeWindow(now()) };
  const subs = new Set();
  let running = false;
  let abort = null;
  let pumping = false;
  let calSaveTimer = null;
  let retryTimer = null;
  let streamLive = false;
  // The keys this client sent, and the plan versions its own changes made: a
  // version conflict is retried on its own only when every change since the
  // command's base was this client's (#65); anyone else's change is the
  // stale-edit guard doing its job, and the athlete decides.
  const sentKeys = new Set();
  const ownVersions = new Set();
  const keyOf = (idem) => (typeof idem === 'string' ? idem.slice(idem.lastIndexOf(':') + 1) : null);

  const set = (patch) => {
    state = { ...state, ...(typeof patch === 'function' ? patch(state) : patch) };
    subs.forEach((fn) => { try { fn(); } catch { /* a subscriber's problem */ } });
  };
  const saveQueue = () => persist.set(K.queue, state.pending.filter((c) => c.status !== 'confirmed')).catch(() => {});
  // Kept with the rest, so an answered accept or a rebuild asked for doesn't offer its buttons again after a reload (#66).
  const saveOutcomes = () => persist.set(K.outcomes, keepOutcomes(state.outcomes)).catch(() => {});
  const saveCal = () => {
    clearTimeout(calSaveTimer);
    calSaveTimer = setTimeout(() => { if (state.cal) persist.set(K.cal, state.cal).catch(() => {}); }, 1000);
  };

  // ---- applying events -----------------------------------------------------------
  // The command's own event confirms it even while its POST is still out: the server
  // commits the item before it answers, so the stream can deliver it first (a message
  // showed twice, its copy "Sending…", until the 202 came back; Sam, 2026-10-13).
  const confirmBy = (cal, event) => (c) => {
    if (c.status !== 'sent' && c.status !== 'sending') return c;
    const mine = event?.idempotency_key && event.idempotency_key.endsWith(`:${c.key}`);
    const seen = c.status === 'sent' && c.seq != null && cal.seq >= c.seq;
    return mine || seen ? { ...c, status: 'confirmed', confirmedAt: Date.now() } : c;
  };

  // Throws SeqGap (the caller syncs) or BadEvent (the caller reads the
  // calendar again). What applied before either is kept.
  function applyEvents(events) {
    let { cal } = state;
    let liveNow = state.live;
    let pending = state.pending;
    let buildEnded = false;
    const settledItems = [];
    let bad = null;
    for (const e of events) {
      const before = cal.seq;
      try {
        const nextCal = apply(cal, e);           // throws SeqGap
        // An event at or below our seq (a sync and the stream both carrying it,
        // a wake-up replay) changed nothing: its text is already in the bubble (#65).
        if (nextCal.seq === before) continue;
        const nextLive = applyLive(liveNow, e);
        cal = nextCal;
        liveNow = nextLive;
      } catch (err) {
        if (err instanceof SeqGap) throw err;
        // An event this app can't apply: replaying it would throw again, for
        // good, and the seq would never move (the 29 Oct wedge). Say so and
        // read the calendar instead, which starts past it.
        console.error(`v2: could not apply event ${e?.seq} (${e?.type})`, err);
        bad = new BadEvent(e, err);
        break;
      }
      pending = pending.map(confirmBy(cal, e));
      if (e.type === 'change.applied' && e.payload?.version != null && sentKeys.has(keyOf(e.idempotency_key))) {
        ownVersions.add(e.payload.version);
      }
      if (endsPlanBuild(e)) buildEnded = true;
      // An offer the athlete asked to rebuild from that is no longer open:
      // its "Your coach is on it…" goes with it.
      if (e.type === 'thread.item' && e.payload?.id != null && e.payload.state !== 'active') settledItems.push(String(e.payload.id));
    }
    if (cal !== state.cal || liveNow !== state.live || pending !== state.pending) {
      set({ cal, live: liveNow, pending });
      saveCal();
    }
    // A build that ended ends every rebuild asked for, the calendar's and each
    // offer's: a per-item one stayed beside old Build / Try again buttons.
    const stale = Object.keys(state.outcomes).filter((id) => state.outcomes[id]?.status === 'rebuilding'
      && (buildEnded || settledItems.includes(id)));
    if (stale.length) {
      set((s) => {
        const outcomes = { ...s.outcomes };
        stale.forEach((id) => delete outcomes[id]);
        return { outcomes };
      });
      saveOutcomes();
    }
    if (bad) throw bad;
  }

  // ---- reads -----------------------------------------------------------------------
  /** GET /v2/strategy: its body, null when the athlete has no v2 strategy (404), undefined when unread. */
  const readStrategy = () => (api.strategy
    ? api.strategy().then((r) => r.data).catch((e) => (e?.response?.status === 404 ? null : undefined))
    : Promise.resolve(undefined));

  async function readCalendar() {
    const { from, to } = state.window;
    const [{ data: cal }, thread, strategy] = await Promise.all([
      api.calendar(from, to),
      api.thread().then((r) => r.data).catch(() => null),
      readStrategy(),
    ]);
    const items = thread ? Object.fromEntries(thread.items.map((i) => [String(i.id), i])) : state.cal?.thread || {};
    set({ cal: fromCalendar(cal, { thread: items, checkins: state.cal?.checkins || {}, strategy, prev: state.cal }) });
    set((s) => ({ pending: s.pending.map(confirmBy(s.cal, null)) }));
    saveCal();
  }

  // One sync at a time: focus, visibilitychange and a conflict retry used to
  // start one each. A call while one runs asks for one more pass after it.
  let syncing = null;
  let syncAgain = false;
  function sync() {
    if (syncing) { syncAgain = true; return syncing; }
    syncing = (async () => {
      try {
        do { syncAgain = false; await syncOnce(); } while (syncAgain);
      } finally {
        syncing = null;
      }
    })();
    return syncing;
  }

  async function syncOnce() {
    if (!state.cal) return readCalendar();
    const { data } = await api.sync(state.cal.seq);
    if (data.snapshot) return readCalendar();       // too far behind to replay
    try {
      applyEvents(data.events || []);
    } catch (e) {
      if (e instanceof SeqGap || e instanceof BadEvent) return readCalendar();
      throw e;
    }
    if ((data.events || []).some((e) => changesStrategy(e) || (e.type === 'draft.status' && e.payload?.stage === 'accepted'))) wantStrategy();
    return undefined;
  }

  /** Re-read /v2/calendar for the conflict badges, which events don't carry. */
  async function refreshConflicts() {
    const { from, to } = state.window;
    try {
      const { data } = await api.calendar(from, to);
      set((s) => ({ cal: s.cal ? { ...s.cal, conflicts: data.conflicts || [] } : s.cal }));
    } catch { /* the next read brings them */ }
  }

  /** Re-read the committed strategy once a draft is accepted: the stream carries the draft, not the result. */
  async function refreshStrategy() {
    const read = await readStrategy();
    if (read === undefined) return;
    set((s) => (s.cal ? { cal: { ...s.cal, strategy: read && strategyFromReads(read, null, s.cal).strategy } } : {}));
    saveCal();
  }

  // M6: the strategy's weeks (written or outline) and the on-track readings
  // change without an event of their own. A plan change, the coach saying
  // how the athlete is tracking, or a phase review re-reads them; a burst of
  // these while one read is in flight costs one more read, not one each.
  let strategyReading = null;
  let strategyAgain = false;
  function wantStrategy() {
    if (strategyReading) { strategyAgain = true; return; }
    strategyReading = refreshStrategy().catch(() => {}).finally(() => {
      strategyReading = null;
      if (strategyAgain) { strategyAgain = false; wantStrategy(); }
    });
  }

  // ---- the stream ------------------------------------------------------------------
  async function streamOnce() {
    abort = new AbortController();
    const response = await api.openStream(state.cal.seq, abort.signal);
    if (response.status === 409) return 'sync';
    if (!response.ok) throw Object.assign(new Error(`stream ${response.status}`), { status: response.status });
    set({ status: 'live' });
    streamLive = true;
    pump();
    let conflictsDue = false;
    try {
      await readSSE(response, (event) => {
        if (event.seq == null) return;
        applyEvents([event]);
        if (event.type === 'change.applied') conflictsDue = true;
        if (event.type === 'draft.status' && event.payload?.stage === 'accepted') wantStrategy();
        else if (changesStrategy(event)) wantStrategy();
      }, { stallMs: STALL_MS });
    } finally {
      streamLive = false;
      if (conflictsDue) refreshConflicts();
    }
    return 'ended';
  }

  async function run() {
    let failures = 0;
    while (running) {
      try {
        if (!state.cal) await readCalendar();
        else await sync();
        const how = await streamOnce();
        if (how === 'sync') continue;
        failures = 0;
      } catch (e) {
        if (!running) break;
        if (e instanceof SeqGap) continue;             // sync, then reconnect, at once
        // A BadEvent backs off like any failure, then the sync reads the
        // calendar again: never a tight loop, even if that read lags the event.
        if (e?.name === 'AbortError') continue;
        failures += 1;
        set({ status: win?.navigator?.onLine === false ? 'offline' : 'reconnecting' });
        const base = BACKOFF_MS[Math.min(failures - 1, BACKOFF_MS.length - 1)];
        await sleep(base / 2 + random() * base);
      }
    }
  }

  // ---- the command queue -------------------------------------------------------------
  /** A command that didn't get through (5xx, 429, no network) is tried again on a timer, not left until the next reconnect (#65). */
  function retryLater(tries) {
    if (!running || retryTimer) return;
    const base = BACKOFF_MS[Math.min(Math.max(tries, 1) - 1, BACKOFF_MS.length - 1)];
    retryTimer = schedule(() => { retryTimer = null; if (running) pump(); }, base / 2 + random() * base);
  }

  /** Every change between `base` and `current` was one of this client's own commands. */
  function ownSince(base, current) {
    if (base == null || current == null || current <= base || current - base > 50) return false;
    for (let v = base + 1; v <= current; v += 1) if (!ownVersions.has(v)) return false;
    return true;
  }

  async function pump() {
    if (pumping || readOnly) return;
    pumping = true;
    try {
      for (;;) {
        const next = state.pending.find((c) => c.status === 'queued');
        if (!next) break;
        const body = next.versioned ? { ...(next.body || {}), base_version: next.base_version ?? undefined } : next.body;
        update(next.key, { status: 'sending' });
        sentKeys.add(next.key);
        let res;
        try {
          res = await api.command({ method: next.method, path: next.path, body, key: next.key });
        } catch (e) {
          if (e?.network) {                         // offline: keep it, try again on reconnect (or the timer)
            update(next.key, { status: 'queued' });
            set({ status: 'offline' });
            retryLater((next.tries || 0) + 1);
            break;
          }
          throw e;
        }
        if (res.status >= 200 && res.status < 300) {
          if (state.status === 'offline') set({ status: streamLive ? 'live' : 'reconnecting' });
          if (res.data?.version != null) ownVersions.add(res.data.version);
          const seq = res.data?.seq ?? null;
          const already = state.pending.find((c) => c.key === next.key)?.status === 'confirmed';
          update(next.key, already ? { seq, result: res.data } : { status: seq == null ? 'confirmed' : 'sent', seq,
            result: res.data, ...(seq == null ? { confirmedAt: Date.now() } : {}) });
          if (state.cal && seq != null && state.cal.seq >= seq) update(next.key, { status: 'confirmed', confirmedAt: Date.now() });
          followUp(next, res.data);
          const outcome = outcomeFor(next, res.data);
          if (outcome) {
            set((s) => ({ outcomes: { ...s.outcomes, [String(next.op.id)]: outcome } }));
            saveOutcomes();
          }
        } else if (alreadyGone(next, res.status)) {
          gone(next);
        } else if (settledConflict(next, res.status, res.data) === 'success') {
          update(next.key, { status: 'confirmed', confirmedAt: Date.now() });   // already done: what they wanted
        } else if (settledConflict(next, res.status, res.data) === 'final') {
          refuse(next, res, { retry: false });
        } else if (res.status === 409 && next.versioned && !next.conflictRetried && !collidingSessions(res.data)
          && await conflictIsOurs(next, res)) {
          await retryConflict(next, res);
        } else if (REFUSED.has(res.status) || (next.tries || 0) + 1 >= MAX_TRANSIENT_TRIES) {
          refuse(next, res);
        } else {
          const tries = (next.tries || 0) + 1;
          update(next.key, { status: 'queued', tries });
          retryLater(tries);
          break;                                    // a server error: the timer (or a reconnect) tries again
        }
      }
    } finally {
      pumping = false;
      saveQueue();
    }
  }

  function update(key, patch) {
    set((s) => ({ pending: s.pending.map((c) => (c.key === key ? { ...c, ...patch } : c)) }));
  }

  function refuse(cmd, res, extra = {}) {
    const sessions = collidingSessions(res.data);
    set((s) => ({
      pending: s.pending.filter((c) => c.key !== cmd.key),
      failed: [...s.failed, { key: cmd.key, kind: cmd.kind, label: cmd.label, subject: cmd.subject, reason: backendReason(res.data),
        status: res.status, op: cmd.op, command: commandOf(cmd), ...(sessions ? { sessions } : {}),
        // no AI coaching consent (aithlo-backend#64): the prompt is open; Try again works once accepted
        ...(isAiConsentRequired(res.status, res.data) ? { friendly: AI_CONSENT_FAILURE_TEXT } : {}),
        // a stale proposal (409 proposal_stale): its card now offers Refresh; a second Accept can't work
        ...(isProposalStale(res.status, res.data) ? { friendly: PROPOSAL_STALE_TEXT, retry: false } : {}), ...extra }],
    }));
  }

  /**
   * A new session's `then` (commands.create): once it exists, the edit and
   * the tick its form also asked for, queued against its id.
   */
  function followUp(cmd, data) {
    const id = data?.session_ids?.[0];
    if (!cmd.then || id == null) return;
    const session = { id, title: cmd.op?.title };
    for (const t of cmd.then) {
      if (t.fields && Object.keys(t.fields).length) send(v2Commands.edit(session, t.fields));
      if (t.done) send(v2Commands.done(session, true));
    }
  }

  /** A delete of something already gone: what the athlete wanted. Drop it, and the row if we still had it. */
  function gone(cmd) {
    const map = goneFrom(cmd);
    set((s) => {
      const rows = s.cal?.[map];
      const id = String(cmd.op?.id ?? '');
      const cal = rows && id in rows ? { ...s.cal, [map]: Object.fromEntries(Object.entries(rows).filter(([k]) => k !== id)) } : s.cal;
      return { cal, pending: s.pending.filter((c) => c.key !== cmd.key) };
    });
    saveCal();
  }

  /** The version a 409 says is current, else the one a sync brings. */
  async function conflictVersion(res) {
    const v = currentVersion(res.data);
    if (v != null) return v;
    try { await sync(); } catch { /* the version we have, then */ }
    return state.cal?.version ?? null;
  }

  /** A 409 caused only by this client's own earlier commands (an offline queue replaying). */
  async function conflictIsOurs(cmd, res) {
    return ownSince(cmd.base_version ?? null, await conflictVersion(res));
  }

  /** A 409 on a versioned command we caused ourselves: once, with the current version. */
  async function retryConflict(cmd, res) {
    const version = await conflictVersion(res);
    update(cmd.key, { key: newKey(), status: 'queued', base_version: version, conflictRetried: true });
  }

  /** Queue a command (commands.js). Returns its key; the calendar shows it at once. Read-only: null, nothing queued. */
  function send(command, sameKey = null) {
    if (readOnly) return null;
    const dup = duplicateOf(state.pending, command);
    if (dup) return dup.key;                        // the same tap again: the first is still on its way
    const key = sameKey || newKey();
    const cmd = { ...command, key, status: 'queued', createdAt: Date.now(),
      base_version: command.versioned ? state.cal?.version ?? null : undefined };
    set((s) => ({ pending: [...s.pending, cmd] }));
    saveQueue();
    pump();
    return key;
  }

  /** The screen has re-read what these commands changed: stop overlaying them. */
  function settle(keys) {
    const ks = new Set(keys);
    set((s) => ({ pending: s.pending.filter((c) => !(c.status === 'confirmed' && ks.has(c.key))) }));
  }

  /**
   * A plan build the athlete started outside the queue (onboarding's
   * /v2/onboarding/complete): the thread shows the coach on it from now,
   * before the run's first event, until the build ends (endsPlanBuild) or
   * BUILD_WAIT_MS pass (calendarRoutes.planBuildAsked). `since` (ms): when it
   * was started; a build that already ended since then isn't expected again.
   */
  function expectBuild(since = Date.now()) {
    if (readOnly) return;
    const ended = Object.values(state.cal?.thread || {}).some((i) => {
      const at = Date.parse(i.created_at);
      return Number.isFinite(at) && at >= since - 5000
        && (i.type === 'proposal' || (i.type === 'coach_message' && REBUILD_KINDS.includes(i.body?.kind)));
    });
    if (ended) return;
    set((s) => ({ outcomes: { ...s.outcomes, [PLAN_BUILD_ID]: { status: 'rebuilding', since } } }));
    saveOutcomes();
  }

  function dismissFailure(key) {
    set((s) => ({ failed: s.failed.filter((f) => f.key !== key) }));
  }

  /** Try again: the same command, queued afresh (a new Idempotency-Key, the version the athlete sees now). */
  function retry(key) {
    const f = state.failed.find((x) => x.key === key);
    if (!f?.command || f.retry === false) return null;   // refused for good: a second try can't work
    dismissFailure(key);
    // After a server error the first try may have committed: the same key
    // makes the second one a replay, not a second message (#65). A refusal
    // (4xx) is a new attempt, with a new key and the version seen now.
    const transient = f.status == null || f.status >= 500 || f.status === 429;
    return send(f.command, transient ? f.key : null);
  }

  const onWake = () => {
    if (!running) return;
    if (win?.document?.visibilityState === 'hidden') return;
    sync().catch(() => {});
    pump();
  };
  const onOnline = () => {
    if (!running) return;
    abort?.abort();                                 // reconnect now rather than at the end of a backoff
    pump();
  };

  let settleTimer = null;

  return {
    subscribe(fn) { subs.add(fn); return () => subs.delete(fn); },
    getSnapshot: () => state,
    readOnly,
    send, settle, dismissFailure, retry, sync, pump, expectBuild,
    async start() {
      if (running) return;
      running = true;
      const [cal, queue, outcomes] = await Promise.all([persist.get(K.cal).catch(() => null), persist.get(K.queue).catch(() => null),
        persist.get(K.outcomes).catch(() => null)]);
      if (cal && state.cal == null) set({ cal });
      if (outcomes && typeof outcomes === 'object') set((s) => ({ outcomes: { ...outcomes, ...s.outcomes } }));
      if (!readOnly && Array.isArray(queue) && queue.length) {
        queue.forEach((c) => sentKeys.add(c.key));
        // Anything mid-flight when the app closed is re-sent: the key makes that safe.
        set((s) => ({ pending: [...queue.map((c) => ({ ...c, status: c.status === 'sent' ? 'sent' : 'queued' })), ...s.pending] }));
      }
      win?.addEventListener?.('visibilitychange', onWake);
      win?.addEventListener?.('focus', onWake);
      win?.addEventListener?.('online', onOnline);
      settleTimer = setInterval(() => {
        const cutoff = Date.now() - SETTLE_AFTER_MS;
        if (state.pending.some((c) => c.status === 'confirmed' && c.confirmedAt < cutoff)) {
          set((s) => ({ pending: s.pending.filter((c) => !(c.status === 'confirmed' && c.confirmedAt < cutoff)) }));
        }
      }, 5000);
      run();
    },
    stop() {
      running = false;
      abort?.abort();
      clearInterval(settleTimer);
      clearTimeout(calSaveTimer);
      if (retryTimer) { unschedule(retryTimer); retryTimer = null; }
      win?.removeEventListener?.('visibilitychange', onWake);
      win?.removeEventListener?.('focus', onWake);
      win?.removeEventListener?.('online', onOnline);
    },
  };
}
